import Pusher from 'pusher-js'
import { createPusherGlobalInstance } from './pusherUtils'

export class ChatroomChannelEventBinder {
  handledChannelEvents = [
    'message_created',
    'message_error',
    'chatroom_name_updated',
    'chatroom_association_updated',
    'chatroom_members_updated',
    'custom_itinerary_updated',
    'ai_active_status_update',
    'ai_activity_update_start',
    'search_activities',
    'search_accommodations',
    'message_reattempt',
    'message_streaming',
    'message_stream_completed',
    'ai_info_collection_thinking',
    'ai_info_collection_typing',
    'itinerary_request_updated',
  ]
  handledUserEvents = ['chatroom_created', 'chatroom_removed', 'chatroom_archived']

  constructor(pusher) {
    this.pusher = pusher
  }

  bind(user, chatroomIds, bindings, { isSupplier = false } = {}) {
    const channelPrefix = 'private-chatroom-'
    // Users and suppliers have distinct channel namespaces so their slugs can
    // never collide on one channel name.
    const userChannelPrefix = isSupplier ? 'private-supplier-' : 'private-user-'
    const userChannelName = userChannelPrefix + user?.slug

    if (user?.slug) {
      let userChannel = this.pusher.subscribe(userChannelName)

      // on new chatroom creation, add channel to store and subscribe event bindings
      Object.entries(bindings).forEach(([eventName, callback]) => {
        if (this.handledUserEvents.includes(eventName)) {
          userChannel.bind(eventName, payload => {
            let channelId = payload.data.id
            callback(payload)

            switch (eventName) {
              case 'chatroom_removed':
                this.pusher.unsubscribe(channelPrefix + channelId)
                break
              case 'chatroom_created':
                let chatroomChannel = this.pusher.subscribe(channelPrefix + channelId)
                this.bindChannel(chatroomChannel, bindings)
                break
              default:
                return
            }
          })
        }
      })
    }

    // set up event bindings for each chatroom
    chatroomIds.forEach(id => {
      let channelName = channelPrefix + id

      const subscribedChannels = this.pusher
        .allChannels()
        .filter(c => c.subscribed)
        .map(c => c.name)

      subscribedChannels
        .filter(c => c.startsWith(channelPrefix) && c == channelName)
        .forEach(c => this.pusher.unsubscribe(c))

      let chatroomChannel = this.pusher.subscribe(channelName)
      this.bindChannel(chatroomChannel, bindings)
    })

    this.pusher.connect()
  }

  bindChannel(channel, bindings) {
    Object.entries(bindings).forEach(([eventName, callback]) => {
      if (this.handledChannelEvents.includes(eventName))
        channel.bind(eventName, data => {
          callback(data)
        })
    })
  }
}

export function useChatPusher() {
  const pusher = createPusherGlobalInstance()

  const unsubscribeAllChannels = () =>
    pusher
      .allChannels()
      .filter(c => c.subscribed)
      .map(c => c.name)
      .forEach(c => pusher.unsubscribe(c))

  const binder = new ChatroomChannelEventBinder(pusher)

  return {
    pusher,
    unsubscribeAllChannels,
    binder,
  }
}
